import axiosDefault from "axios";
import {v4 as uuidv4} from "uuid";
import {policyTokenUrl} from "../app.config";
import {fetchNewToken, getStoredToken, storeToken} from "./tokenService";

// Align headers and options on the global axios instance so interceptors apply
axiosDefault.defaults.headers.common["Content-Type"] = "application/json";
axiosDefault.defaults.headers.common["clientId"] = "ui-demo";
axiosDefault.defaults.headers.common["correlationID"] = uuidv4();
axiosDefault.defaults.withCredentials = true;

// Token handling and interceptor setup
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// get the token endpoint URL or undefined if not set
function getTokenEndpoint(): string | undefined {
  try {
    return policyTokenUrl() || undefined;
  } catch {
    return undefined;
  }
}

/*
returns true if the given URL is the get:token endpoint URL
* */
function isTokenEndpoint(url?: string): boolean {
  const tokenUrl = getTokenEndpoint();
  if (!url || !tokenUrl) return false;
  try {
    return url.includes(tokenUrl);
  } catch {
    return false;
  }
}

/*
*  Ensure a valid token is available, fetching a new one if needed or forced
* */
async function ensureToken(forceRefresh: boolean): Promise<string> {
  const existing = getStoredToken();
  if (!forceRefresh && existing) return existing; // return existing if not forcing refresh
  if (isRefreshing && refreshPromise) return refreshPromise; // wait for ongoing refresh
  isRefreshing = true;
  refreshPromise = fetchNewToken().finally(() => {
    isRefreshing = false;
  });
  try {
     // wait for the refresh to complete
      return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/*
*  axios request interceptor to add Authorization header with Bearer token if not already present
* */
axiosDefault.interceptors.request.use(
  async (config) => {
    const cfg: any = config as any;
    const url: string | undefined = cfg.url as string | undefined;
    if (!isTokenEndpoint(url)) {
      let token = getStoredToken();
      if (!token) {
        try {
          token = await ensureToken(false);
        } catch (e) {
          // if token fetch fails, continue without header; server may return 401 to trigger refresh in response interceptor
        }
      }
      if (token) {
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${token}`;
      }
    }
    /*
    * if the request is to the token endpoint, we add the Authorization header with the basic token */
    if (isTokenEndpoint(url)) {
       cfg.headers = cfg.headers || {};
       cfg.headers.Authorization = process.env.REACT_APP_POLICY_BASIC_TOKEN;
    }
    return cfg;
  },
  (error) => Promise.reject(error),
);

/*
*  axios response interceptor to handle 401 responses by attempting a token refresh and retrying the request once
* */
axiosDefault.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: any = error?.config as any;
    const status = error?.response?.status;
    const url: string | undefined = originalRequest?.url;

    if (status === 401 && originalRequest && !originalRequest._retry && !isTokenEndpoint(url)) {
      originalRequest._retry = true;
      try {
        const token = await ensureToken(true);
        // Update header for retry
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosDefault(originalRequest);
      } catch (e) {
        // hard reset token if refresh flow fails
        storeToken(null);
        return Promise.reject(e);
      }
    }

    return Promise.reject(error);
  },
);

// Export the configured global axios so call sites using this module get the same interceptors
export const axios = axiosDefault;
